/* eslint-disable no-useless-escape */
import { required, requiredIf, minValue, maxValue, sameAs, email, url, helpers } from "@vuelidate/validators";
const { withParams, withMessage, withAsync } = helpers;
import { isFieldVisible } from "@elements/formFields/utils";
import dayjs from "@/plugins/dayjs";
import { isRef, unref } from "vue";
import { cloneDeep } from "lodash-es";
import { dateValidation, ibanValidation, bankAccountValidation, priceDistributionValidation, customRegexValidation, businessRegistryCodeValidation, personRegistryCodeValidation, swiftBicValidation, vatCodeValidation, uniquenessValidation } from "@/mixins/custom_validation";
export function setupVuelidateRules(initForm, formTracker, meta) {
    const { t, mapToModel, formTrackerName = "form", customTranslationErrorLabels } = meta;
    // eslint-disable-next-line prefer-const
    let { vuelidateValidations, readOnlyFields, formFields = [] } = initForm;
    vuelidateValidations = cloneDeep(vuelidateValidations);
    function mutateFieldName(fieldName) {
        if (mapToModel && meta.mapToModel[fieldName]) {
            fieldName = meta.mapToModel[fieldName];
        }
        return fieldName;
    }
    if ("incomeTaxCardFile" in vuelidateValidations) {
        vuelidateValidations.incomeTaxCardFile = injectFileRequiredCondition("incomeTaxCardFile", vuelidateValidations.incomeTaxCardFile, isRef(formTracker) ? formTracker.value : formTracker, {
            filePathName: "incomeTaxCardFilePath",
            fileRemoveName: "incomeTaxCardFileRemove"
        });
    }
    return {
        [formTrackerName]: Object.entries(vuelidateValidations).reduce((rules, [fieldName, value]) => {
            // Ignore validations on disabled/readonly fields
            let formField = formFields.find(x => x.name == fieldName);
            if (readOnlyFields.includes(fieldName) ||
                (formField && !isFieldVisible(unref(formTracker), formField))) {
                rules[fieldName] = {};
                return rules;
            }
            fieldName = mutateFieldName(fieldName);
            rules[fieldName] = {};
            if (value.validationRegex) {
                const regexPattern = value.validationRegex;
                rules[fieldName].validationRegex = withMessage(({ $model }) => t("common:formValidation.invalid", "Invalid") + " " + $model, withParams({ type: "validationRegex" }, validationValue => customRegexValidation(validationValue, regexPattern)));
            }
            if (value.email || value.emailValidation) {
                rules[fieldName].email = withMessage(() => {
                    var _a, _b;
                    if ((_a = customTranslationErrorLabels === null || customTranslationErrorLabels === void 0 ? void 0 : customTranslationErrorLabels[fieldName]) === null || _a === void 0 ? void 0 : _a.email) {
                        return t((_b = customTranslationErrorLabels === null || customTranslationErrorLabels === void 0 ? void 0 : customTranslationErrorLabels[fieldName]) === null || _b === void 0 ? void 0 : _b.email);
                    }
                    return t("common:formValidation.emailValidation", "Invalid email format");
                }, email);
            }
            if (typeof value.requiredIf === "string") {
                const requiredIfFieldName = value.requiredIf;
                rules[fieldName].requiredIfPropHas = withMessage(() => t("common:formValidation.requiredField", "Should be filled"), withParams({}, (v, siblings) => {
                    if (v || v === 0)
                        return true;
                    if (!!siblings[requiredIfFieldName] === true)
                        return false;
                    return true;
                }));
            }
            else if (value.requiredIf) {
                rules[fieldName].requiredIf = withMessage(() => t("common:formValidation.requiredField", "Should be filled"), requiredIf(value.requiredIf));
            }
            if (value.requiredWhenPropHas) {
                const { propName, values } = value.requiredWhenPropHas;
                rules[fieldName].requiredWhenPropHas = withMessage(() => t("common:formValidation.requiredField", "Should be filled"), withParams({}, (v, siblings) => requiredWhenPropHasCondition(v, propName, values, siblings)));
            }
            if (value.required) {
                rules[fieldName].required = withMessage(() => t("common:formValidation.requiredField", "Should be filled"), required);
            }
            if (value.vatCode) {
                rules[fieldName].vatCode = withAsync(withMessage(() => t("common:formValidation.vatCode", "Invalid VAT code"), (value, siblings) => vatCodeValidation(value, siblings)), () => {
                    var _a;
                    return isRef(formTracker)
                        ? // @ts-ignore
                            (_a = formTracker.value) === null || _a === void 0 ? void 0 : _a.registryCountryId
                        : formTracker === null || formTracker === void 0 ? void 0 : formTracker.registryCountryId;
                } // watches this property changes to force trigger this validation again
                );
            }
            if (value.dateValidation) {
                rules[fieldName].dateValidation = withParams({
                    type: "dateValidation"
                }, dateValidation);
                if (value.maxValue) {
                    const max = Date.parse(value.maxValue);
                    const maxV = value.maxValue;
                    rules[fieldName].maxValue = withMessage(({ $params }) => {
                        if (dayjs().format("YYYY-MM-DD") == $params.maxISO) {
                            return t("common:formValidation.dateValidation.cannotBeInFuture", "Date cannot be in future");
                        }
                        else {
                            return t([
                                "common:formValidation.dateValidation.cannotBeLaterThan",
                                "Date cannot be later than {{ maxValue }}"
                            ], {
                                maxValue: dayjs.date($params.maxISO),
                                interpolation: { escapeValue: false }
                            });
                        }
                    }, withParams({
                        type: "maxValue",
                        max: max,
                        maxISO: maxV
                    }, v => {
                        if (!v)
                            return true;
                        if (dayjs(v).isAfter(maxV)) {
                            return false;
                        }
                        return true;
                    }));
                }
                if (value.minValue) {
                    const min = Date.parse(value.minValue);
                    const minV = value.minValue;
                    rules[fieldName].minValue = withMessage(() => {
                        return t("common:formValidation.dateValidation.cannotBeInPast", "Date cannot be in past");
                    }, withParams({
                        type: "minValue",
                        min: min,
                        minISO: minV
                    }, v => {
                        if (!v)
                            return true;
                        if (dayjs(v).isBefore(minV)) {
                            return false;
                        }
                        return true;
                    }));
                }
            }
            else {
                if (value.maxValue || value.maxValue == 0) {
                    rules[fieldName].maxValue = withMessage(() => t("common:formValidation.maxValue", `Number max reached`), maxValue(value.maxValue));
                }
                if (value.minValue || value.minValue == 0) {
                    if (typeof value.minValue === "object") {
                        const minValueObj = value.minValue;
                        rules[fieldName].minValue = withMessage(() => minValueObj.message, minValue(minValueObj.minValue));
                    }
                    else {
                        rules[fieldName].minValue = withMessage(() => t("common:formValidation.minValue", `Number cannot go futher down`), minValue(value.minValue));
                    }
                }
            }
            if (value.priceDistributionValidation) {
                rules[fieldName].priceDistributionValidation = withMessage(() => t("common:formValidation.priceDistributionValidation", "Price distribution cannot contain letters"), withParams({
                    type: "priceDistributionValidation"
                }, priceDistributionValidation));
            }
            if (value.minLength) {
                const min = value.minLength;
                rules[fieldName].minLength = withMessage(({ $params }) => t([
                    "common:formValidation.minLength",
                    `Should be at least {\{ minLength }\} characters long`
                ], {
                    minLength: $params.min
                }), withParams({
                    type: "minLength",
                    min: min
                }, (v) => {
                    if (!v)
                        return true;
                    else if (Array.isArray(v))
                        return v.length >= min;
                    return v.replace(/<[^>]*>/g, "").length >= min; // Exclude html tags
                }));
            }
            if (value.maxLength) {
                const max = value.maxLength;
                rules[fieldName].maxLength = withMessage(({ $params }) => t([
                    "common:formValidation.maxLength",
                    `Cannot be over {\{ maxLength }\} characters long`
                ], {
                    maxLength: $params.max
                }), withParams({
                    type: "maxLength",
                    max: max
                }, (v) => {
                    if (!v)
                        return true;
                    else if (Array.isArray(v))
                        return v.length <= max;
                    return v.replace(/<[^>]*>/g, "").length <= max; // Exclude html tags
                }));
            }
            if (value.ibanValidation == true) {
                rules[fieldName].ibanValidation = withMessage(() => t("common:formValidation.ibanValidation", "Invalid IBAN format."), withParams({ type: "ibanValidation" }, ibanValidation));
            }
            if (value.bankAccountValidation == true) {
                rules[fieldName].bankAccountValidation = withMessage(() => t("common:formValidation.bankAccountValidation", "Invalid BBAN or IBAN format."), withParams({ type: "bankAccountValidation" }, value => bankAccountValidation(value)));
            }
            if (value.personRegistryCode == true) {
                rules[fieldName].personRegistryCode = withMessage(() => t("common:formValidation.personRegistryCodeValidation", `Invalid personal registry code`), withParams({ type: "personRegistryCode" }, (value, siblings) => personRegistryCodeValidation(value, siblings)));
            }
            if (value.organisationRegistryCode == true) {
                rules[fieldName].organisationRegistryCode = withMessage(() => t("common:formValidation.organisationRegistryCode", `Invalid business registry code`), withParams({ type: "organisationRegistryCode" }, (value, siblings) => businessRegistryCodeValidation(value, siblings)));
            }
            if (value.swiftBicCode) {
                rules[fieldName].swiftBicCode = withMessage(() => t("common:formValidation.swiftBicCode", `Invalid BIC`), withParams({ type: "swiftBicCode" }, swiftBicValidation));
            }
            if (value.url == true) {
                rules[fieldName].url = withMessage(() => t("common:formValidation.url", "Invalid URL"), url);
            }
            if (value.sameAsPassword) {
                rules[fieldName].sameAsPassword = withMessage(() => t("signin:newpassword.passwordsDoNotMatch", "Passwords do not match"), sameAs(isRef(formTracker)
                    ? formTracker.value[value.sameAsPassword]
                    : formTracker[value.sameAsPassword]));
            }
            if (value.uniqueness) {
                rules[fieldName].uniqueness = withMessage(() => t("common:formValidation.uniqueness", `Not unique`), withParams({ type: "uniquness" }, (v, siblings) => uniquenessValidation(v, {
                    siblings,
                    fieldNames: value.uniqueness
                })));
            }
            return rules;
        }, {})
    };
}
/**
 *
 * @param {any} value field value itself
 * @param {string} propName property name that depends on whether this field is required or not
 * @param {array} requiredWhenValues values that should return value true based on propName property
 * @param {object} siblings form object
 * @returns boolean - false will trigger validation error
 */
function requiredWhenPropHasCondition(value, propName, requiredWhenValues, siblings) {
    if (!requiredWhenValues || requiredWhenValues.length == 0)
        return true;
    if ((siblings[propName] != undefined || siblings[propName] != null) &&
        requiredWhenValues.includes(siblings[propName])) {
        return value != null && value != undefined ? true : false;
    }
    return true;
}
/**
 * Dedicated to workaround uploaded file that has *Path (like incomeTaxCardFilePath) value
 * should not give out error over *File (like incomeTaxCardFile)
 * Also checks if selected to remove, then check if there is new file selected
 *
 * @param {any} value eg incomeTaxCardFile property value
 * @param {object} siblings form's other properties
 * @param {object} fieldNames
 * filePathName {string} - name of field that should contain path to file.
 *
 * fileRemoveName {boolean} - name of field that determines whether file is to be deleted;
 *
 * @returns boolean for vuelidate's requiredIf built-in validator
 *
 * true if marked for delete but no new file added or there just is no file seletced
 *
 * false if file is selected or has *Path field value.
 */
function fileRequiredCondition(value, siblings, { filePathName, fileRemoveName }) {
    if (siblings[fileRemoveName] === true && !value)
        return true; // return true so requiredIf will validate the need to have new file selected
    if (siblings[filePathName])
        return false; // return false so requiredIf will not validate it
    return !value ? true : false; // if no value, return true so requiredIf will validate. otherwise false
}
/**
 * Replaces required and requiredWhenPropHas to requiredIf, where it checks if its required and then
 * checks if file has been selected or has url path instead
 *
 * NB! cloneDeep whole vuelidateValidations object or original data will dissapear!
 *
 * @param {string} propertyKey field name to get value from siblings object
 * @param {object} ruleValidation rule validations from server how to validate field
 * @param {object} siblings form object
 * @param {object} param3
 * filePathName - field name to get file path value (like incomeTaxCardFilePath to indicate file is uploaded);
 *
 * fileRemoveName - field name (incomeTaxCardFileRemove) to indicate if set to be removed
 * @returns ruleValidation that replaces required and requiredWhenHasProps to requiredIf
 */
export function injectFileRequiredCondition(propertyKey, ruleValidation, siblings, { filePathName, fileRemoveName }) {
    var _a;
    let isRequired = (_a = ruleValidation.required) !== null && _a !== void 0 ? _a : false;
    if (!isRequired && ruleValidation.requiredWhenPropHas) {
        const { propName, values } = ruleValidation.requiredWhenPropHas;
        isRequired = !requiredWhenPropHasCondition(siblings[propertyKey], propName, values, siblings);
    }
    ruleValidation.requiredIf = () => {
        if (!isRequired)
            return false; // when its not required at all, return false so requiredIf won't validate
        return fileRequiredCondition(siblings[propertyKey], siblings, {
            filePathName,
            fileRemoveName
        });
    };
    delete ruleValidation.required;
    delete ruleValidation.requiredWhenPropHas;
    return ruleValidation;
}
